import { inject, Ref, ref, onMounted, onUnmounted } from 'vue';
import { PolicyFormDetails, InteractiveFormsReady, PolicyFormsTracker, PolicyFormsGenerated, DocumentStorageConfig, DocumentsReadyResponse, ViewFormDetailsResponse, FormSystem, StatusUpdate } from '@/models/PrintDocumentModel';
import { MessageService, DecodedEnvelope } from '@/contracts/MessageService';
import { Message, OperationResult } from '@/contracts/OperationResult';
import { useCommercialApi } from '@/composables/UseCommercialApi';
import { TelemetryContract } from '@/contracts/TelemetryContract';
import { useFetch } from '@/composables/UseFetch';
import { CommercialApiConfig } from '@/services/CommercialApiConfig'
import type { Tell } from '@/services/Tell';
import { DocGenInteractiveMessages } from '@/domain/messaging/DocGenMessaging';
import { UnhandledOperationError } from '@/domain/errors/CustomErrors';
import { MessageProperties, createSerializedEnvelope } from '@/domain/messaging/MessageEnvelope';
import { EntraAuthService } from '@/services/EntraAuthService';
import { useClientMessaging } from '@/composables/UseClientMessaging';
import { EventBus, Events } from '@/contracts/EventBus';
import { request } from 'http';

export function usePrintDocumentsLogic(
	policyNumber: Ref<string | null>,
	amendmentNumber: Ref<number | null>
) {
	const tell = inject('tell') as Tell;
	const telemetry = inject('telemetry') as TelemetryContract;
	const eventBus = inject('eventBus') as EventBus;
	//const messageService = inject('messageService') as MessageService;
	const entraAuth = inject('auth') as EntraAuthService;
	const disposers: Array<() => void> = [];

	const { docsDocGenRequest, getPolicyForms, getCompletedPolicyForms, putInteractiveForms, editCompletedInteractiveForms } = useCommercialApi(CommercialApiConfig);
	const { toOperationResult, fetchResilientWithAuth } = useFetch();
	const interactiveForms = ref<PolicyFormDetails[]>([]);
	const policyFormsTracker = ref<PolicyFormsTracker | null>(null);
	const documentStorageConfig = ref<DocumentStorageConfig | null>(null);
	const generatedFormDetails = ref<ViewFormDetailsResponse[]>(null); //important to initialize as null
	const progressUpdate = ref<number | null>(null);
	const statusMessage = ref<string>('');
	const errorMessages = ref<Message[]>([]);
	const correlationId = ref<string | null>(null);

	function addUniqueError(errorMsg: string) {
		const isDuplicate = errorMessages.value.some(
			existing => existing.content === errorMsg && existing.type === 'error',
		);
		if (!isDuplicate) {
			errorMessages.value.push({ type: 'error', content: errorMsg });
		}
	}

	// Create message filter function for policy number validation
	const messageFilter = (envelope: DecodedEnvelope<unknown>, methodName: string): boolean => {
		try {
			return ((envelope.properties as unknown as Record<string, unknown>)?.['CorrelationId'] as string || envelope.properties?.correlationId) === correlationId.value;
			
		} catch (error) {
			telemetry.trackTrace({
				message: `Error in message filter for ${methodName}: ${error instanceof Error ? error.message : 'Unknown error'}`,
				severityLevel: telemetry.severityLevel.Error,
			});
			
			// On error, be safe and filter out the message
			return false;
		}
	};

	// Create a messaging client for the 'documents' hub with message filtering
	const {
		onMessage
	} = useClientMessaging({ 
		hubName: 'documents', 
		messageFilter 
	});

	onMounted(async () => {
		//we only want a new correlationId when the page loads
		correlationId.value = crypto.randomUUID();

		await registerMessagingHandlers();
		await requestDocumentsForPolicy();
	});

	onUnmounted(async () => {
		await unregisterMessagingHandlers();
	});

	const requestDocumentsForPolicy = async () => {
		try {
			const account = await entraAuth.getActiveAccount();
			const request = createSerializedEnvelope(
				"MoE.Commercial.Api.Models.Messages.RequestDocumentsMessage",
				{
					requestDocumentsPolicyNumber: policyNumber.value,
					requestDocumentsAmendmentNumber: amendmentNumber.value,
					isBatch: false
				},
				new MessageProperties({
					correlationId: correlationId.value,
					customProperties:
					{
						UserId: account.username,
						HubName: "documents"
					}
				})
		);

		await docsDocGenRequest<unknown, OperationResult<void>>(request, null);

		console.log("Requesting documents for policy:", policyNumber.value);
		} catch (error) {
			// Log the error with telemetry
			telemetry.trackTrace({
				message: `Error requesting documents for policy ${policyNumber.value}: ${error instanceof Error ? error.message : 'Unknown error'}`,
				severityLevel: telemetry.severityLevel.Error,
			});
		}
	};

	const checkForFormGenerationErrors = (tracker: PolicyFormsTracker) => {
		if (!tracker.policyForms || tracker.policyForms.length === 0) {
			return;
		}

		for (const form of tracker.policyForms) {
			const errorMsg = form.formGenerationStatus?.errorMessage;
			if (errorMsg && errorMsg.trim() !== '') {
				addUniqueError(errorMsg);
			}
		}
	};

	const onReceiveInteractiveForms = async (
		envelope: DecodedEnvelope<InteractiveFormsReady>
	) => {

		const payload = envelope.decoded;

		if (!payload?.documentId) {
			throw new UnhandledOperationError('No document ID received.');
		}

		const forms = await getPolicyForms<PolicyFormsTracker>(payload.documentId, envelope.properties?.correlationId);

		checkForFormGenerationErrors(forms.data);

		//save a reference to all forms
		policyFormsTracker.value = forms.data;

		eventBus.emit(Events.INTERACTIVE_FORMS_GENERATED);

		interactiveForms.value = forms.data.policyForms
			.filter(f => f.isInteractive === true);

		if (interactiveForms.value.length === 0) {
			await notifyInteractiveFormsComplete();
		}

		telemetry.trackTrace({
			message: `Received ${interactiveForms.value.length} interactive forms (documentId=${policyFormsTracker.value?.id})`,
			severityLevel: telemetry.severityLevel.Information,
		});
	};

	const restoreInteractiveForms = async (): Promise<boolean> => {
		if (!policyFormsTracker.value?.policyForms) {
			return false;
		}
		console.log("Restoring interactive forms for documentId:", policyFormsTracker.value);

		try {
			// Call the API to edit completed interactive forms
			const result = await editCompletedInteractiveForms(policyFormsTracker.value);
			
			if (!result.success) {
				console.error('Failed to edit completed interactive forms:', result.messages);
				return false;
			}

			//re-request documents
			await requestDocumentsForPolicy();
		} catch (error) {
			console.error('Error calling editCompletedInteractiveForms:', error);
			return false;
		}
	};

	const onStatusUpdate = async (
		envelope: DecodedEnvelope<StatusUpdate>
	) => {

		const payload = envelope.decoded;

		progressUpdate.value = payload.percentComplete;
		statusMessage.value = payload.message;
	};


	const onPolicyFormsGenerated = async (
		envelope: DecodedEnvelope<PolicyFormsGenerated>
	) => {
		try {
			const payload = envelope.decoded;

			if (!payload?.documentId) {
				throw new UnhandledOperationError('No document ID received.');
			}

			// Use already known correlationId from earlier interactive phase if available
			const forms = await getCompletedPolicyForms<DocumentsReadyResponse>(payload.documentId, correlationId.value);

			checkForFormGenerationErrors(forms.data.tracker);

			//save a reference to incoming entity
			policyFormsTracker.value = forms.data.tracker;
			documentStorageConfig.value = forms.data.storageConfig

			eventBus.emit(Events.INTERACTIVE_FORMS_GENERATED);

			//clear out interactive forms
			// if(errorMessages.value.length === 0){
			// 	interactiveForms.value = [];
			// }

			generatedFormDetails.value = policyFormsTracker.value.policyForms
				.filter(form => {
					//only show GhostDraft forms that rendered successfully
					return form.formSystem === FormSystem.GhostDraft && form.formGenerationStatus?.dateCompleted != null;
				}).map(form => {
					return {
						documentUrl: //build url using storage config
							`${documentStorageConfig.value?.storageAccountUri}/${documentStorageConfig.value?.containerName}/${documentStorageConfig.value?.path}` +
							`/${form.formName}.pdf` +
							`${documentStorageConfig.value?.sasToken}`,
						formName: form.ghostDraftFormName || form.formName
					};
				});

			if (generatedFormDetails.value.length === 0 && policyFormsTracker.value.hasInteractiveForms) {
				generatedFormDetails.value = null; // Set to null if no forms are generated
			}

			eventBus.emit(Events.INTERACTIVE_FORMS_GENERATED);

			telemetry.trackTrace({
				message: `Received policy forms for documentId=${policyFormsTracker.value?.id}`,
				severityLevel: telemetry.severityLevel.Information,
			});
		} catch (error) {
			// Log the error with telemetry
			telemetry.trackTrace({
				message: `Error in onPolicyFormsGenerated for documentId=${envelope.decoded?.documentId}: ${error instanceof Error ? error.message : 'Unknown error'}`,
				severityLevel: telemetry.severityLevel.Error,
			});

			// Add error to errorMessage array for user display
			const errorMsg = error instanceof Error ? error.message : 'Failed to retrieve completed policy forms';
			addUniqueError(errorMsg);
		}
	};

	const notifyInteractiveFormsComplete = async () => {
		const account = await entraAuth.getActiveAccount();
		const request = createSerializedEnvelope(
			"MoE.Commercial.Api.Models.Messages.NotifyInteractiveFormsCompleteMessage",
			{
				documentId: policyFormsTracker.value?.id,
				interactiveFormsCompletedPolicyNumber: policyNumber.value
			},
			new MessageProperties({
				correlationId: correlationId.value,
				customProperties:
				{
					UserId: account.username,
					HubName: "documents"
				}
			})
		);

		await docsDocGenRequest<unknown, OperationResult<void>>(request, null);
	};

	const registerMessagingHandlers = async () => {
		disposers.push(
			onMessage<InteractiveFormsReady>(
				DocGenInteractiveMessages.InteractiveFormsReady,
				onReceiveInteractiveForms
			)
		);

		disposers.push(
			onMessage<PolicyFormsGenerated>(
				DocGenInteractiveMessages.PolicyFormsGenerated,
				onPolicyFormsGenerated
			)
		);

		disposers.push(
			onMessage<StatusUpdate>(
				DocGenInteractiveMessages.StatusUpdate,
				onStatusUpdate
			)
		);
	};

	const unregisterMessagingHandlers = async () => {
		for (const dispose of disposers) {
			try { dispose(); } catch { /* noop */ }
		}
		disposers.length = 0;

		telemetry.trackTrace({
			message: `Unsubscribed from DocGen interactive handlers`,
			severityLevel: telemetry.severityLevel.Verbose,
		});
	};

	return {
		interactiveForms,
		policyFormsTracker,
		generatedFormDetails,
		notifyInteractiveFormsComplete,
		errorMessages,
		progressUpdate,
		statusMessage,
		restoreInteractiveForms
	};
}
